import { Injectable, Scopes, ControllerProtocol } from '@visiba/instantiation';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { BookingScreen, ViewBookingAnalyticActions, ViewBookingController } from '../view_booking.controller';
import { BookingBasketService } from '../booking_basket.service';
import { signal } from '@preact/signals-react';
import { NiNFormatter, NiN } from '@visiba/national-identity-number';

@Injectable({
	scope: Scopes.Scoped,
})
export class ScreenWhoIsRespondantController implements ControllerProtocol {
	public data = signal<PresentationModel>({ respondentType: null, representativeNin: null, representativeNinPlaceholder: '' });

	constructor(
		private readonly viewBookingController: ViewBookingController,
		private readonly analyticService: AnalyticService,
		private readonly bookingBasketService: BookingBasketService,
	) {
		const basket = bookingBasketService.get();
		this.data.value = {
			respondentType: basket.respondantType,
			representativeNin: basket.representativeNin,
			representativeNinPlaceholder: '',
		};
	}

	async whenMount() {
		this.analyticService.trackPageView({
			customTitle: 'Booking: Who is respondant',
		});

		this.bookingBasketService.unlockTimeSlot();

		// If the licenceholder does not allow representative, skip this screen
		if (!this.viewBookingController.flowData.peek().value?.allowRepresentative) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.DoesNotAllowRepresentative);
			this.bookingBasketService.set({
				respondantType: 'me',
				representativeNin: null,
			});

			this.viewBookingController.goToScreen(BookingScreen.ReasonForVisit, { replace: true });

			return;
		}

		// If representative is set by flow settings, skip this screen
		const flowSettings = this.viewBookingController.getFlowSettings();
		if (flowSettings.representativeNin) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.AutoSetRespondantTypeRepresentative);
			this.bookingBasketService.set({
				respondantType: 'representative',
				representativeNin: flowSettings.representativeNin,
			});

			this.viewBookingController.goToScreen(BookingScreen.ReasonForVisit, { replace: true });

			return;
		}

		// If there is a red robin session id, but no representative nin, skip this screen
		if (flowSettings.redRobinSessionId && !flowSettings.representativeNin) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.AutoSetRespondantTypeMe);
			this.bookingBasketService.set({
				respondantType: 'me',
				representativeNin: null,
			});

			this.viewBookingController.goToScreen(BookingScreen.ReasonForVisit, { replace: true });

			return;
		}

		const basket = this.bookingBasketService.get();
		this.data.value = {
			respondentType: basket.representativeNin ? 'representative' : 'me',
			representativeNin: basket.representativeNin,
			representativeNinPlaceholder:
				NiNFormatter.formatWithPlaceholder(this.viewBookingController.getFlowData().currentCountry, '')[1] ?? '',
		};
	}

	public async submit(data: FormData) {
		const flowData = this.viewBookingController.getFlowData();

		this.bookingBasketService.set({
			respondantType: data.respondentType === 'representative' ? 'representative' : 'me',
			representativeNin:
				data.respondentType === 'representative'
					? NiNFormatter.format(flowData.currentCountry, NiN.normalize(flowData.currentCountry, (data.representativeNin ?? '').trim()))
					: null,
		});

		this.viewBookingController.goToScreen(BookingScreen.ReasonForVisit);
	}
}

export type RespondentType = 'me' | 'representative';

interface PresentationModel {
	respondentType: Nullable<RespondentType>;
	representativeNin: Nullable<string>;
	representativeNinPlaceholder: string;
}

interface FormData {
	respondentType: RespondentType;
	representativeNin: Nullable<string>;
}
